export type BlobDisplayNameInput = {
  blobId?: string | null;
  occurrenceName?: string | null;
  canonicalName?: string | null;
  originalName?: string | null;
  fallbackName?: string | null;
  mimeType?: string | null;
};

export type BlobRenameParts = {
  editableName: string;
  extension: string;
};

const DEFAULT_BLOB_NAME = 'download';

const MIME_EXTENSION_MAP: Record<string, string> = {
  'application/json': 'json',
  'application/msword': 'doc',
  'application/pdf': 'pdf',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.ms-powerpoint': 'ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    'pptx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'docx',
  'application/xml': 'xml',
  'application/zip': 'zip',
  'audio/mpeg': 'mp3',
  'audio/ogg': 'ogg',
  'audio/wav': 'wav',
  'image/gif': 'gif',
  'image/heic': 'heic',
  'image/heif': 'heif',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/svg+xml': 'svg',
  'image/webp': 'webp',
  'text/csv': 'csv',
  'text/html': 'html',
  'text/markdown': 'md',
  'text/plain': 'txt',
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
  'video/webm': 'webm',
};

function normalizeName(value: string | null | undefined): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed || trimmed === '.' || trimmed === '..') {
    return null;
  }
  return trimmed;
}

function pickName(input: BlobDisplayNameInput): string {
  return (
    normalizeName(input.occurrenceName) ??
    normalizeName(input.canonicalName) ??
    normalizeName(input.originalName) ??
    normalizeName(input.fallbackName) ??
    normalizeName(input.blobId) ??
    DEFAULT_BLOB_NAME
  );
}

function mimeExtension(mimeType: string | null | undefined): string | null {
  if (!mimeType) {
    return null;
  }
  const normalized = mimeType.toLowerCase().split(';', 1)[0]?.trim();
  if (!normalized) {
    return null;
  }
  return MIME_EXTENSION_MAP[normalized] ?? null;
}

export function inferMimeTypeFromName(
  name: string | null | undefined
): string | null {
  const normalized = normalizeName(name);
  if (!normalized) return null;
  const lastSeparator = Math.max(
    normalized.lastIndexOf('/'),
    normalized.lastIndexOf('\\')
  );
  const lastDot = normalized.lastIndexOf('.');
  if (lastDot <= lastSeparator + 1 || lastDot >= normalized.length - 1) {
    return null;
  }
  const extension = normalized.slice(lastDot + 1).toLowerCase();
  if (extension === 'jpeg') return 'image/jpeg';
  const entry = Object.entries(MIME_EXTENSION_MAP).find(
    ([, candidate]) => candidate === extension
  );
  return entry?.[0] ?? null;
}

function hasFilenameExtension(name: string): boolean {
  const lastSeparator = Math.max(name.lastIndexOf('/'), name.lastIndexOf('\\'));
  const lastDot = name.lastIndexOf('.');
  return lastDot > lastSeparator + 1 && lastDot < name.length - 1;
}

function withMimeExtension(name: string, mimeType: string | null | undefined) {
  if (hasFilenameExtension(name)) {
    return name;
  }
  const extension = mimeExtension(mimeType);
  return extension ? `${name}.${extension}` : name;
}

function sanitizeDownloadName(name: string): string {
  const sanitized = name
    .replace(/[\u0000-\u001f\u007f/\\:*?"<>|]+/g, '_')
    .replace(/\s+/g, ' ')
    .trim();
  return normalizeName(sanitized) ?? DEFAULT_BLOB_NAME;
}

function sanitizeRenameEditableName(name: string): string | null {
  if (!/[^\u0000-\u001f\u007f/\\:*?"<>|\s]/.test(name)) {
    return null;
  }
  const sanitized = name
    .replace(/[\u0000-\u001f\u007f/\\:*?"<>|]+/g, '_')
    .replace(/\s+/g, ' ')
    .trim();
  return normalizeName(sanitized);
}

export function resolveBlobDisplayName(input: BlobDisplayNameInput): string {
  return withMimeExtension(pickName(input), input.mimeType);
}

export function resolveBlobDownloadName(input: BlobDisplayNameInput): string {
  return withMimeExtension(
    sanitizeDownloadName(resolveBlobDisplayName(input)),
    input.mimeType
  );
}

export function splitBlobNameForRename(displayName: string): BlobRenameParts {
  const name = normalizeName(displayName) ?? DEFAULT_BLOB_NAME;
  if (!hasFilenameExtension(name)) {
    return { editableName: name, extension: '' };
  }
  const lastDot = name.lastIndexOf('.');
  return {
    editableName: name.slice(0, lastDot),
    extension: name.slice(lastDot),
  };
}

export function composeBlobNameFromRename(
  editableName: string,
  extension: string
): string | null {
  const normalized = normalizeName(editableName);
  if (!normalized) {
    return null;
  }
  const sanitized = sanitizeRenameEditableName(normalized);
  return sanitized ? `${sanitized}${extension}` : null;
}
